import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { authHeaders, guidesFetch } from './guidesUtils';
import './guides.css';

const MANAGE_ROLES = ['director', 'admin', 'administrative assistant'];

function readUserRole() {
  try {
    const direct = localStorage.getItem('role');
    if (direct) return direct;
    return JSON.parse(localStorage.getItem('user') || '{}')?.role || null;
  } catch {
    return null;
  }
}

export default function GuideSettingsPage() {
  const canManage = useMemo(() => MANAGE_ROLES.includes(readUserRole()), []);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [topicModal, setTopicModal] = useState(null);
  const [topicName, setTopicName] = useState('');
  const [renameModal, setRenameModal] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await guidesFetch('/categories', { headers: authHeaders() });
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const seedDefaults = async () => {
    setBusy(true);
    try {
      const res = await guidesFetch('/categories/seed-defaults', {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({})
      });
      setCategories(res.categories || []);
      toast.success(res.created ? `Added ${res.created} specialties` : 'All default specialties already present');
    } catch (err) {
      toast.error(err.message || 'Failed to load defaults');
    } finally {
      setBusy(false);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) return;
    setBusy(true);
    try {
      const created = await guidesFetch('/categories', {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ name: categoryName.trim() })
      });
      setCategories((prev) => [...prev, created]);
      setCategoryModal(false);
      setCategoryName('');
      toast.success('Category added');
    } catch (err) {
      toast.error(err.message || 'Failed to add category');
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async (e) => {
    e.preventDefault();
    if (!renameModal || !renameValue.trim()) return;
    setBusy(true);
    try {
      if (renameModal.kind === 'category') {
        const updated = await guidesFetch(`/categories/${renameModal.id}`, {
          method: 'PUT',
          headers: authHeaders(true),
          body: JSON.stringify({ name: renameValue.trim() })
        });
        setCategories((prev) => prev.map((c) => (c.id === updated.id ? { ...c, name: updated.name } : c)));
      } else {
        const updated = await guidesFetch(`/taxonomy-topics/${renameModal.id}`, {
          method: 'PUT',
          headers: authHeaders(true),
          body: JSON.stringify({ name: renameValue.trim() })
        });
        setCategories((prev) => prev.map((c) => {
          if (c.id !== renameModal.categoryId) return c;
          return {
            ...c,
            topics: (c.topics || []).map((t) => (t.id === updated.id ? { ...t, name: updated.name } : t))
          };
        }));
      }
      setRenameModal(null);
      toast.success('Updated');
    } catch (err) {
      toast.error(err.message || 'Failed to rename');
    } finally {
      setBusy(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category and all its topics?')) return;
    try {
      await guidesFetch(`/categories/${id}`, { method: 'DELETE', headers: authHeaders() });
      setCategories((prev) => prev.filter((c) => c.id !== id));
      toast.success('Category deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const addTopic = async (e) => {
    e.preventDefault();
    if (!topicModal || !topicName.trim()) return;
    setBusy(true);
    try {
      const created = await guidesFetch(`/categories/${topicModal}/topics`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ name: topicName.trim() })
      });
      setCategories((prev) => prev.map((c) => (
        c.id === topicModal
          ? { ...c, topics: [...(c.topics || []), created] }
          : c
      )));
      setTopicModal(null);
      setTopicName('');
      toast.success('Topic added');
    } catch (err) {
      toast.error(err.message || 'Failed to add topic');
    } finally {
      setBusy(false);
    }
  };

  const deleteTopic = async (categoryId, topicId) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await guidesFetch(`/taxonomy-topics/${topicId}`, { method: 'DELETE', headers: authHeaders() });
      setCategories((prev) => prev.map((c) => (
        c.id === categoryId
          ? { ...c, topics: (c.topics || []).filter((t) => t.id !== topicId) }
          : c
      )));
      toast.success('Topic deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete topic');
    }
  };

  return (
    <div className="guides-list-page">
      <div className="guides-list-header">
        <div>
          <h6 className="mb-0 fw-semibold">Settings</h6>
          <p className="guides-page-meta">Manage categories and topics used when publishing</p>
        </div>
        {canManage && (
          <div className="guides-list-actions">
            <Button size="sm" variant="outline-secondary" onClick={seedDefaults} disabled={busy}>
              Load default specialties
            </Button>
            <Button size="sm" variant="primary" onClick={() => { setCategoryName(''); setCategoryModal(true); }}>
              Add category
            </Button>
          </div>
        )}
      </div>

      {!canManage && (
        <p className="small text-muted mb-3">
          Categories are managed by program directors and admins. You can view them here for publishing.
        </p>
      )}

      {loading ? (
        <div className="guides-loading"><Spinner /></div>
        ) : categories.length === 0 ? (
        <div className="guides-empty card border shadow-none p-4">
          <p className="mb-3 text-muted">
            {canManage
              ? 'No categories yet. Load the default medical specialties to get started.'
              : 'No categories yet. Ask a director or admin to set them up.'}
          </p>
          {canManage && (
            <Button variant="primary" size="sm" onClick={seedDefaults} disabled={busy}>
              Load default specialties
            </Button>
          )}
        </div>
      ) : (
        <Accordion className="guides-accordion">
          {categories.map((cat) => (
            <Accordion.Item eventKey={String(cat.id)} key={cat.id} className="guides-section-card">
              <div className="guides-section-card-header accordion-header">
                <Accordion.Button className="guides-section-card-toggle">
                  <span className="fw-semibold">{cat.name}</span>
                  {cat.isDefault && <span className="guides-badge draft ms-2">Default</span>}
                </Accordion.Button>
                {canManage && (
                  <div className="guides-section-card-actions">
                    <Button
                      size="sm"
                      variant="light-lite"
                      className="px-2 py-1"
                      title="Add topic"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTopicModal(cat.id);
                        setTopicName('');
                      }}
                    >
                      <i className="bi bi-plus-lg" />
                    </Button>
                    <Button
                      size="sm"
                      variant="light-lite"
                      className="px-2 py-1"
                      title="Rename"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setRenameModal({ kind: 'category', id: cat.id });
                        setRenameValue(cat.name);
                      }}
                    >
                      <i className="bi bi-pencil" />
                    </Button>
                    <Button
                      size="sm"
                      variant="light-lite danger-lite"
                      className="px-2 py-1"
                      title="Delete"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteCategory(cat.id);
                      }}
                    >
                      <i className="bi bi-trash" />
                    </Button>
                  </div>
                )}
              </div>
              <Accordion.Body className="p-0">
                {(cat.topics || []).length === 0 ? (
                  <div className="text-muted small px-3 py-3">No topics yet{canManage ? '. Add optional topics under this specialty.' : '.'}</div>
                ) : (
                  <div className="guides-topic-rows">
                    {(cat.topics || []).map((topic) => (
                      <div key={topic.id} className="guides-topic-row-item custom-hover-bg-alt">
                        <div className="guides-topic-row-main px-3">
                          <span className="fw-semibold d-block">{topic.name}</span>
                        </div>
                        {canManage && (
                          <div className="d-flex gap-1 pe-2">
                            <Button
                              size="sm"
                              variant="light-lite"
                              className="px-2 py-1"
                              onClick={() => {
                                setRenameModal({ kind: 'topic', id: topic.id, categoryId: cat.id });
                                setRenameValue(topic.name);
                              }}
                            >
                              <i className="bi bi-pencil" />
                            </Button>
                            <Button
                              size="sm"
                              variant="light-lite danger-lite"
                              className="px-2 py-1"
                              onClick={() => deleteTopic(cat.id, topic.id)}
                            >
                              <i className="bi bi-trash" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <Modal show={categoryModal} onHide={() => setCategoryModal(false)} centered>
        <Form onSubmit={addCategory}>
          <Modal.Header closeButton><Modal.Title>Add category</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Control
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g. Cardiology"
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setCategoryModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Add</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!topicModal} onHide={() => setTopicModal(null)} centered>
        <Form onSubmit={addTopic}>
          <Modal.Header closeButton><Modal.Title>Add topic</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Control
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              placeholder="Topic name"
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setTopicModal(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Add</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!renameModal} onHide={() => setRenameModal(null)} centered>
        <Form onSubmit={saveRename}>
          <Modal.Header closeButton>
            <Modal.Title>Rename {renameModal?.kind === 'topic' ? 'topic' : 'category'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setRenameModal(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
